import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { ServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionStatus';
import { IChargeTokenResponse } from '@wix/cashier-core/dist/src/services/Payment/types';
import { PaymentCompleteResult } from '../types/PaymentCompleteResult';
import {
  isFailedServerStatus,
  isSuccessfulServerStatus,
  mapServerToClientStatus,
} from './statusUtils';
import { filterDefined } from '../../utils/filterDefined';

export class PaymentCompleteResultBuilder {
  private result: PaymentCompleteResult = {
    __isPaymentCompleteResult: true,
    clientStatus: 'Unknown',
  } as PaymentCompleteResult;

  private extend = (partialResult: Partial<PaymentCompleteResult>) => {
    this.result = filterDefined<PaymentCompleteResult>({
      ...this.result,
      ...partialResult,
    } as PaymentCompleteResult);
    return this;
  };

  withPaymentMethod = (paymentMethod: PaymentMethod) =>
    this.extend({ paymentMethod });
  withServerStatus = (serverStatus: ServerTransactionStatus) => {
    const clientStatus = mapServerToClientStatus(serverStatus);

    return this.extend({
      status: serverStatus,
      serverStatus: serverStatus as any,
      clientStatus,
      ...(clientStatus !== 'Unknown' && {
        isSuccessOrPending: isSuccessfulServerStatus(serverStatus),
        _isPaymentFailed: isFailedServerStatus(serverStatus),
      }),
    });
  };
  withRedirectUrl = (redirectUrl: string) => this.extend({ redirectUrl });
  withChargeId = (chargeId: string) => this.extend({ chargeId });
  withErrorCode = (errorCode: string) => this.extend({ errorCode });
  withErrorMessage = (errorMessage: string) =>
    this.extend({ translatedError: errorMessage || undefined }); // undefined to prevent from empty errorMessage
  withChargeResponse = (chargeResponse: IChargeTokenResponse) =>
    this.withChargeInitiated()
      .withChargeId(chargeResponse.chargeId)
      .withRedirectUrl(chargeResponse.redirectUrl)
      .withServerStatus(chargeResponse.transactionStatus)
      .withErrorCode(chargeResponse.failureCode);
  withInternalError = (error: any) => this.extend({ internalError: error });
  withUnexpectedInternalError = (error: any) =>
    this.extend({ isUnexpectedFailure: true }).withInternalError(error);
  withChargeInitiated = () => this.extend({ isChargeInitiated: true });

  mergeResult = (result: Partial<PaymentCompleteResult>) =>
    this.extend({ ...result });

  build(): PaymentCompleteResult {
    return { ...this.result };
  }
}

export const createPaymentCompleteResultBuilder = () =>
  new PaymentCompleteResultBuilder();

export const isPaymentCompleteResult = (data: any): boolean =>
  data?.__isPaymentCompleteResult === true;
